import { useRef, type ReactNode } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';
import { useReducedMotion } from '@/lib/motion/useReducedMotion';
import { cx } from '@/lib/cx';
import styles from './ParallaxLayer.module.css';

export interface ParallaxLayerProps {
  children: ReactNode;
  /** Recorrido total en px a lo largo del scroll. Muy leve por defecto (doc 04). */
  distance?: number;
  className?: string;
}

/**
 * Parallax MINIMO para fondos de hero (galaxia, cielo). El doc 04 permite
 * "parallax minimo" y prohibe scroll-jacking: por eso el recorrido por defecto
 * son 40px y el movimiento es puramente un `translateY` (GPU, sin reflow).
 * Con movimiento reducido, la capa queda quieta pero VISIBLE.
 */
export function ParallaxLayer({ children, distance = 40, className }: ParallaxLayerProps) {
  const ref = useRef<HTMLDivElement>(null);
  const reduced = useReducedMotion();
  const { scrollYProgress } = useScroll({ target: ref, offset: ['start end', 'end start'] });
  const y = useTransform(scrollYProgress, [0, 1], [-distance / 2, distance / 2]);

  if (reduced) {
    return (
      <div ref={ref} className={cx(styles.layer, className)}>
        {children}
      </div>
    );
  }

  return (
    <div ref={ref} className={cx(styles.layer, className)}>
      <motion.div className={styles.inner} style={{ y }}>
        {children}
      </motion.div>
    </div>
  );
}
